/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { useParams } from "react-router-dom";
import { Spacer, theme } from "@nordcloud/gnui";
import { CostBox } from "~/components/CostBox";
import { useCurrency, useDisclosure } from "~/hooks";
import { isNotEmpty, isNotNil } from "~/tools";
import { AccountCredentialsUpdateSidebar } from "../AccountCredentialsUpdateSidebar/AccountCredentialsUpdateSidebar";
import {
  HybridOnboardSidebar,
  ClusterUtilization,
  ChargeTypeCostsBar,
} from "../components";
import {
  getAccountDisplayName,
  isKubernetesOrOpenshift,
  mapProviderTab,
} from "../utils";
import {
  Details,
  DetailsForm,
  DetailsHeader,
  DetailsNotification,
  HybridCloudDocumentationSidebar,
} from "./Details";
import { EstateRecords } from "./Estate/EstateRecords";
import { Header } from "./Header";
import { useCloudAccountDetails } from "./hooks/useCloudAccountDetails";

export function CloudAccountDetailsPage() {
  const {
    open: openCredentialsUpdateSteps,
    close: closeCredentialsUpdateSteps,
    isOpen: showCredentialsUpdateSteps,
  } = useDisclosure();

  const {
    open: openDocumentationSidebar,
    close: closeDocumentationSidebar,
    isOpen: isOpenDocumentationSidebar,
  } = useDisclosure();

  const {
    open: openOnboardSidebar,
    close: closeOnboardSidebar,
    isOpen: isOpenOnboardSidebar,
  } = useDisclosure();

  const { nid = "" } = useParams<RouteParams>();

  const {
    account,
    tags,
    showUpdateCredentials,
    chargeTypeCosts,
    accountSavingsCosts,
    accountSpends,
  } = useCloudAccountDetails(nid);

  const { currency } = useCurrency();

  const showClusterUtilization = isNotNil(account?.providerType)
    ? isKubernetesOrOpenshift(account?.providerType)
    : false;

  const providerTab = mapProviderTab(account?.provider, account?.providerType);

  return (
    <>
      <When condition={isNotNil(account)}>
        <Header
          tags={tags}
          account={account}
          openCredentialsUpdateSteps={openCredentialsUpdateSteps}
          showUpdateCredentials={showUpdateCredentials}
          openDocumentationSidebar={openDocumentationSidebar}
          openOnboardSidebar={openOnboardSidebar}
        />
        <DetailsNotification
          account={account}
          openCredentialsUpdateSteps={openCredentialsUpdateSteps}
          openDocumentationSidebar={openDocumentationSidebar}
          openOnboardSidebar={openOnboardSidebar}
        />
        <Details>
          <div css={{ width: "70%" }}>
            <DetailsHeader
              accountName={getAccountDisplayName(account)}
              provider={account?.provider}
              providerType={account?.providerType}
              cloudStatus={account?.cloudStatus}
            />
            <DetailsForm
              accountInternalId={nid}
              accountId={account?.providerId}
              accountOwner={account?.owner}
              description={account?.description ?? ""}
              contactsNames={(account?.contacts ?? [])
                .map((contactPerson) => contactPerson?.name ?? "")
                .join(", ")}
            />
          </div>
          <div css={{ width: "30%", height: "18.5rem" }}>
            <CostBox
              numericalValues={{
                forecast: accountSpends.currentMonthForecastValue,
                currentMonth: accountSpends.currentMonthValue,
                previousMonth: accountSpends.previousMonthValue,
                currency,
                budget: 0,
                cloudWasteCost: accountSavingsCosts.cost,
                co2e: accountSpends.currentMonthCo2e,
                co2eSavings: accountSavingsCosts.co2e,
                originalCost: 0,
                reservedInstanceBenefit: 0,
              }}
            />
          </div>
        </Details>
        <When condition={isNotEmpty(chargeTypeCosts)}>
          <Spacer height={theme.spacing.spacing04} />
          <ChargeTypeCostsBar chargeTypeCosts={chargeTypeCosts} />
        </When>
        <When condition={showClusterUtilization}>
          <Spacer height={theme.spacing.spacing04} />
          <ClusterUtilization nordcloudId={account?.nid} />
        </When>
        <EstateRecords
          providerId={account?.providerId}
          providerType={account?.providerType}
          provider={account?.provider}
        />
        <When condition={showUpdateCredentials && isNotNil(providerTab)}>
          {providerTab && (
            <AccountCredentialsUpdateSidebar
              nids={[nid]}
              provider={providerTab}
              isOpen={showCredentialsUpdateSteps}
              onClose={closeCredentialsUpdateSteps}
            />
          )}
        </When>
        <HybridCloudDocumentationSidebar
          account={account}
          isOpen={isOpenDocumentationSidebar}
          onClose={closeDocumentationSidebar}
        />
        <HybridOnboardSidebar
          account={account}
          isOpen={isOpenOnboardSidebar}
          onClose={closeOnboardSidebar}
        />
      </When>
    </>
  );
}

type RouteParams = {
  nid: string;
};
